import { useEffect, useState } from "react";
import { AxiosRequestConfig } from "axios";
import { authAxios } from "../../../utils/session_utils";
import {
    AutopodderResultRow,
    AutopodderResultsResponse,
    ScheduleDataItem,
    ScheduleDataResponse,
} from "../../types";

interface BatchJobCalendarData {
    loading: boolean;
    err: string | null;
    allRows: AutopodderResultRow[];                          // all strategies
    scheduleData: Record<string, ScheduleDataItem>;          // ad inventory dict
    strategies: string[];
}

export function useBatchJobCalendarData(batchJobId: string | null): BatchJobCalendarData {
    const [allRows, setAllRows] = useState<AutopodderResultRow[]>([]);
    const [scheduleData, setScheduleData] = useState<Record<string, ScheduleDataItem>>({});
    const [strategies, setStrategies] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        if (!batchJobId) return;

        let cancelled = false;
        setLoading(true);
        setErr(null);

        const apiBase = process.env.REACT_APP_BACK_END_API;

        const fetchResults = async () => {
            const config: AxiosRequestConfig = {
                method: "GET",
                url: `${apiBase}/snowflake/batch-jobs/autopodder-results?batchJobId=${batchJobId}`,
                headers: { "Content-Type": "application/json" },
            };
            const response = await authAxios(config);
            return response.data as AutopodderResultsResponse;
        };

        const fetchInventory = async () => {
            const config: AxiosRequestConfig = {
                method: "GET",
                url: `${apiBase}/snowflake/batch-jobs/autopodder-ad-inventory?batchJobId=${batchJobId}`,
                headers: { "Content-Type": "application/json" },
            };
            const response = await authAxios(config);
            return response.data as ScheduleDataResponse;
        };

        Promise.all([fetchResults(), fetchInventory()])
            .then(([resultsRes, inventoryRes]) => {
                if (cancelled) return;
                if (resultsRes.err) setErr(resultsRes.err);

                const rows = resultsRes.rows ?? [];
                setAllRows(rows);
                setStrategies(
                    Array.from(new Set(rows.map((r) => r.STRATEGY_NAME))).filter(Boolean) as string[],
                );

                const dict: Record<string, ScheduleDataItem> = {};
                for (const item of inventoryRes.rows ?? []) {
                    dict[item.SCHEDULE_NAME] = item;
                }
                setScheduleData(dict);
            })
            .catch((e: unknown) => {
                if (cancelled) return;
                setErr(e instanceof Error ? e.message : String(e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [batchJobId]);

    return { loading, err, allRows, scheduleData, strategies };
}
